import { SEVERITY_RANK, type Severity } from '@opensentry/shared';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAcknowledgeZone, useZone } from '../hooks/useData.js';
import { api } from '../lib/api.js';
import { countryFlag, daysUntilDeparture, lastFirst, localTime, timeAgo } from '../lib/format.js';
import { CategoryIcon } from './CategoryIcon.js';
import { ExplainWhy } from './ExplainWhy.js';
import { SeverityIcon, SeverityPill } from './SeverityPill.js';
import { SEVERITY_COLORS } from './SeverityPill.js';
import { Button } from './ui/Button.js';
import { Drawer } from './ui/Drawer.js';

/** §21.6 — streamed per-zone posture summary. Calm zones (0 threats)
 *  show a static "all clear" line instead of burning an LLM call. */
function ZoneSummary({ zoneId, hasThreats }: { zoneId: number; hasThreats: boolean }) {
  const [text, setText] = useState('');
  const [state, setState] = useState<'streaming' | 'done' | 'unavailable'>('streaming');

  useEffect(() => {
    if (!hasThreats) return;
    setText('');
    setState('streaming');
    const es = new EventSource(api.zoneSummaryUrl(zoneId));
    es.addEventListener('token', (e) => {
      setText((t) => t + (JSON.parse((e as MessageEvent).data).t ?? ''));
    });
    es.addEventListener('unavailable', () => setState('unavailable'));
    es.addEventListener('done', () => {
      setState((s) => (s === 'unavailable' ? s : 'done'));
      es.close();
    });
    es.onerror = () => es.close();
    return () => es.close();
  }, [zoneId, hasThreats]);

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[var(--color-muted)]">
        <Sparkles size={12} className="text-[var(--color-sev-high)]" /> AI summary
      </div>
      {!hasThreats ? (
        <p className="text-sm text-[var(--color-muted)]">
          All clear — no active threats within range of this zone.
        </p>
      ) : state === 'unavailable' ? (
        <p className="text-xs text-[var(--color-muted)]">
          AI summary unavailable — set ANTHROPIC_API_KEY to enable.
        </p>
      ) : (
        <p className="text-sm">
          {text}
          {state === 'streaming' && <span className="animate-pulse">▋</span>}
        </p>
      )}
    </div>
  );
}

export function ZoneDrawer({ zoneId, onClose }: { zoneId: number | null; onClose: () => void }) {
  const { data, isLoading } = useZone(zoneId);
  const ack = useAcknowledgeZone();

  const maxRank = (data?.matches ?? []).reduce(
    (mx, m) => Math.max(mx, SEVERITY_RANK[m.event.severity]),
    -1,
  );
  const maxSev = (['low', 'medium', 'high', 'critical'] as const)[maxRank] as Severity | undefined;
  const statusColor = maxSev ? SEVERITY_COLORS[maxSev] : '#3f8f5e';
  const threatCount = data?.matches.length ?? 0;

  return (
    <Drawer
      open={zoneId != null}
      onClose={onClose}
      title={
        data ? (
          <span className="flex items-center gap-2">
            {countryFlag(data.zone.countryCode)} {data.zone.label ?? `Zone ${data.zone.id}`}
          </span>
        ) : (
          'Zone'
        )
      }
    >
      {isLoading || !data ? (
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
            <div
              className="flex items-center gap-2 px-3 py-2"
              style={{ background: statusColor, color: '#0b0b0c' }}
            >
              {maxSev ? <SeverityIcon severity={maxSev} size={15} /> : null}
              <span className="text-sm font-bold uppercase tracking-wide">
                {maxSev ? `${maxSev} exposure` : 'All clear'}
              </span>
              <span className="ml-auto text-xs font-semibold">
                {threatCount === 0 ? 'no active threats' : `${threatCount} active threats`}
              </span>
            </div>
            <div className="grid grid-cols-2 divide-x divide-[var(--color-border)] bg-[var(--color-panel-2)]">
              <div className="px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  People in zone
                </div>
                <div className="mono text-xl font-semibold leading-tight">
                  {data.zone.employeeCount}
                </div>
              </div>
              <div className="px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  Location
                </div>
                <div className="mono text-xs leading-tight text-[var(--color-muted)]">
                  {data.zone.centroidLat.toFixed(2)}, {data.zone.centroidLng.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {zoneId != null && <ZoneSummary zoneId={zoneId} hasThreats={threatCount > 0} />}

          {threatCount > 0 && (
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Active threats</h3>
              <Button onClick={() => zoneId != null && ack.mutate(zoneId)} disabled={ack.isPending}>
                Acknowledge all
              </Button>
            </div>
          )}

          <ul className="space-y-2">
            {data.matches.map(({ match, event }) => (
              <li
                key={match.id}
                className={`rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3 transition-opacity ${
                  match.notified ? 'opacity-[0.8]' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <CategoryIcon category={event.category} />
                  <SeverityPill severity={event.severity} />
                  <span className="ml-auto mono text-[11px] text-[var(--color-muted)]">
                    {match.distanceKm.toFixed(0)} km · {timeAgo(event.occurredAt)}
                  </span>
                </div>
                <div className="mt-1 text-sm">{event.title}</div>
                <div className="mono mt-0.5 text-[10px] text-[var(--color-muted)]">
                  {localTime(event.occurredAt, event.lng)}
                </div>
                {event.description && (
                  <p className="mt-1 text-xs text-[var(--color-muted)]">{event.description}</p>
                )}
                <div className="mt-1 flex items-center gap-2 text-[11px] text-[var(--color-muted)]">
                  <span className="uppercase">{event.source}</span>
                  {event.url && (
                    <a href={event.url} target="_blank" rel="noreferrer" className="underline">
                      source
                    </a>
                  )}
                  {match.notified ? <span>· acknowledged</span> : null}
                </div>
                <ExplainWhy eventId={event.id} zoneId={match.zoneId} />
              </li>
            ))}
          </ul>

          <div className="border-t border-[var(--color-border)] pt-3">
            <h3 className="mb-2 text-sm font-semibold">Team in zone ({data.employees.length})</h3>
            <ul className="max-h-56 space-y-1 overflow-y-auto text-xs">
              {data.employees.map((e) => (
                <li key={e.id} className="flex justify-between text-[var(--color-muted)]">
                  <span>
                    {lastFirst(e.name)}
                    {e.role ? ` · ${e.role}` : ''}
                  </span>
                  <span className="mono">departs in {daysUntilDeparture(e.id)}d</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Drawer>
  );
}
